// Finanzlage auf einen Blick — der erste Reiter der Buchhaltung.
//
// Oben drei Zahlen, die die wichtigsten Fragen beantworten: Wie viel Geld
// ist da? Wie lange reicht es? Wie lief der Monat? Darunter der Verlauf — Zeitraum
// frei wählbar, rückblickend und als Vorschau — und die Bausteine dahinter.
//
// Ohne eingetragenen Kontostand fehlt die erste Zahl. Die Seite sagt das
// offen und bietet das Eintragen direkt an, statt eine 0 € hinzuschreiben,
// die wie ein leeres Konto aussähe.
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { euro, datum } from '../../../gemeinsam/lib/daten';
import { Abzeichen, Blickfang, Feld, Hinweis, Karte, KartenKopf, Knopf, Spinner, Auswahl } from '../../../gemeinsam/komponenten/Basis';
import LiquiditaetsVerlauf from './LiquiditaetsVerlauf';

type Konto = {
  id: string; name: string; art: 'bank' | 'kasse'; hauptkonto: boolean; bankverbunden?: boolean;
  standEingetragen: number; standAm: string | null; bewegungSeitdem: number; standGeschaetzt: number; tageAlt: number | null;
};

type Lage = {
  stichtag: string;
  liquiditaet: { gesamt: number; konten: Konto[]; ohneKonto: boolean; nichtZugeordnet: number };
  monat: {
    aktuell: { monat: string; einnahmen: number; ausgaben: number; saldo: number };
    vormonat: { monat: string; einnahmen: number; ausgaben: number; saldo: number };
    schnittEinnahmen: number;
  };
  fixkosten: { proMonat: number; proJahr: number; anzahl: number; anteilAnEinnahmen: number | null;
    betriebsausgabenProMonat: number; nurLiquiditaetProMonat: number;
    naechste: Array<{ datum: string; bezeichnung: string; brutto: number }> };
  forderungen: { offen: number; anzahl: number; ueberfaellig: number; ueberfaelligAnzahl: number };
  verbindlichkeiten?: { offen: number; anzahl: number; ueberfaellig: number; ueberfaelligAnzahl: number };
  umsatzsteuer: { monat: string; umsatzsteuer: number; vorsteuer: number; zahllast: number; faelligAm: string; hinweis: string } | null;
  reichweiteMonate: number | null;
  breakEven: { umsatzNettoProMonat: number; rohertragsquote: number; grundlage: string };
  vorschau: {
    tage: number;
    punkte: Array<{ datum: string; stand: number }>;
    ereignisse: Array<{ datum: string; art: 'forderung' | 'fixkosten' | 'liquiditaet' | 'steuer' | 'verbindlichkeit'; text: string; betrag: number }>;
    in30: number; in60: number; in90: number;
    tiefpunkt: { datum: string; stand: number };
    nichtEingeplant: { betrag: number; anzahl: number };
  };
};

const monatsName = (schluessel: string) => {
  const [j, m] = schluessel.split('-');
  return new Date(Date.UTC(+j, +m - 1, 1)).toLocaleDateString('de-DE', { month: 'long', timeZone: 'UTC' });
};

export default function Finanzlage({ zuFixkosten }: { zuFixkosten: () => void }) {
  const [lage, setLage] = useState<Lage | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = useCallback(async () => {
    try {
      setLage(await api<Lage>('/accounting/finanzlage'));
      setFehler(null);
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Die Finanzlage konnte nicht geladen werden.');
    }
  }, []);

  useEffect(() => { void laden(); }, [laden]);

  if (fehler) return <Hinweis art="fehler">{fehler}</Hinweis>;
  if (!lage) return <div className="flex justify-center py-16"><Spinner /></div>;

  const { liquiditaet: liq, monat, fixkosten: fix, forderungen: ford, vorschau: vs } = lage;
  const reichweiteTon = lage.reichweiteMonate === null ? 'neutral'
    : lage.reichweiteMonate < 3 ? 'rot' : lage.reichweiteMonate < 6 ? 'gelb' : 'gruen';
  const veraenderung = monat.aktuell.saldo - monat.vormonat.saldo;

  return (
    <div className="space-y-4">
      {/* --- Die drei Kernzahlen ------------------------------------------- */}
      <div className="grid gap-3 md:grid-cols-3">
        <Karte klasse="p-5">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Liquidität heute</p>
          {liq.ohneKonto ? (
            <>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">Noch kein Kontostand eingetragen.</p>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Einmal eintragen — danach rechnet die App mit jeder Zahlung weiter.</p>
            </>
          ) : (
            <>
              <p className={`mt-1 text-3xl font-bold tabular-nums ${liq.gesamt < 0 ? 'text-red-600 dark:text-red-400' : 'text-slate-900 dark:text-slate-100'}`}>
                {euro(liq.gesamt)}
              </p>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                {liq.konten.map(k => `${k.name} ${euro(k.standGeschaetzt)}`).join(' · ')}
              </p>
            </>
          )}
        </Karte>

        <Karte klasse="p-5">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Reichweite</p>
          {lage.reichweiteMonate === null ? (
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
              {liq.ohneKonto ? 'Braucht einen Kontostand.' : 'Braucht erfasste Fixkosten.'}
            </p>
          ) : (
            <>
              <p className="mt-1 flex items-baseline gap-2">
                <span className="text-3xl font-bold tabular-nums text-slate-900 dark:text-slate-100">
                  {lage.reichweiteMonate.toLocaleString('de-DE', { maximumFractionDigits: 1 })}
                </span>
                <span className="text-sm text-slate-500 dark:text-slate-400">Monate</span>
                <Abzeichen art={reichweiteTon}>
                  {reichweiteTon === 'rot' ? 'knapp' : reichweiteTon === 'gelb' ? 'beobachten' : 'solide'}
                </Abzeichen>
              </p>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                So lange decken die Mittel die Fixkosten, wenn nichts mehr hereinkäme.
              </p>
            </>
          )}
        </Karte>

        <Karte klasse="p-5">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            {monatsName(monat.aktuell.monat)} bisher
          </p>
          <p className={`mt-1 text-3xl font-bold tabular-nums ${monat.aktuell.saldo < 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
            {monat.aktuell.saldo >= 0 ? '+' : ''}{euro(monat.aktuell.saldo)}
          </p>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {euro(monat.aktuell.einnahmen)} rein · {euro(monat.aktuell.ausgaben)} raus
            {' · '}
            <span className={veraenderung >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}>
              {veraenderung >= 0 ? '▲' : '▼'} {euro(Math.abs(veraenderung))} zu {monatsName(monat.vormonat.monat)}
            </span>
          </p>
        </Karte>
      </div>

      {/* --- Warnung bei Engpass ------------------------------------------ */}
      {!liq.ohneKonto && vs.tiefpunkt.stand < 0 && (
        <Hinweis art="fehler" titel={`Engpass am ${datum(vs.tiefpunkt.datum)}`}>
          Nach heutigem Stand sinkt die Liquidität dort auf {euro(vs.tiefpunkt.stand)}. Offene Rechnungen
          anmahnen, Zahlungen verschieben oder einen Puffer einplanen.
        </Hinweis>
      )}

      {/* --- Verlauf und Vorschau, Zeitraum wählbar --------------------- */}
      <LiquiditaetsVerlauf />

      {/* --- Bausteine ----------------------------------------------------- */}
      <div className="grid gap-3 md:grid-cols-2">
        <Karte klasse="p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Feste Zahlungen</p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900 dark:text-slate-100">
                {euro(fix.proMonat)}<span className="text-sm font-normal text-slate-500"> / Monat</span>
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {euro(fix.proJahr)} im Jahr · {fix.anzahl} Posten
                {fix.anteilAnEinnahmen !== null && ` · ${fix.anteilAnEinnahmen.toLocaleString('de-DE', { maximumFractionDigits: 0 })} % der Einnahmen`}
              </p>
              {fix.nurLiquiditaetProMonat > 0 && (
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {euro(fix.betriebsausgabenProMonat)} Betriebsausgaben · {euro(fix.nurLiquiditaetProMonat)} Steuern, Tilgung, Entnahmen
                </p>
              )}
            </div>
            <Knopf variante="still" className="!text-xs" onClick={zuFixkosten}>Verwalten</Knopf>
          </div>
          {fix.anzahl === 0 ? (
            <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
              Miete, Versicherungen, Leasing einmal anlegen — sie buchen sich danach jeden Monat selbst.
            </p>
          ) : fix.naechste.length > 0 && (
            <ul className="mt-3 space-y-1 text-sm">
              {fix.naechste.slice(0, 4).map((t, i) => (
                <li key={i} className="flex justify-between gap-3">
                  <span className="truncate text-slate-600 dark:text-slate-300">{datum(t.datum)} · {t.bezeichnung}</span>
                  <span className="shrink-0 tabular-nums text-slate-900 dark:text-slate-100">{euro(t.brutto)}</span>
                </li>
              ))}
            </ul>
          )}
        </Karte>

        <Karte klasse="p-5">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Offene Forderungen</p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900 dark:text-slate-100">{euro(ford.offen)}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">{ford.anzahl} Rechnung(en) noch nicht bezahlt</p>
          {ford.ueberfaelligAnzahl > 0 && (
            <p className="mt-3 text-sm text-amber-700 dark:text-amber-400">
              Davon überfällig: {euro(ford.ueberfaellig)} ({ford.ueberfaelligAnzahl})
            </p>
          )}
          {/* Gegenstück: was der Betrieb selbst noch zahlen muss (offene Lieferantenrechnungen). */}
          {lage.verbindlichkeiten && lage.verbindlichkeiten.anzahl > 0 && (
            <div className="mt-4 border-t border-slate-200 pt-3 dark:border-slate-800">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Offene Verbindlichkeiten</p>
              <p className="mt-1 text-xl font-bold tabular-nums text-slate-900 dark:text-slate-100">{euro(lage.verbindlichkeiten.offen)}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {lage.verbindlichkeiten.anzahl} Lieferantenrechnung(en) — in der Vorschau am Fälligkeitstag eingeplant
              </p>
              {lage.verbindlichkeiten.ueberfaelligAnzahl > 0 && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  Überfällig: {euro(lage.verbindlichkeiten.ueberfaellig)} ({lage.verbindlichkeiten.ueberfaelligAnzahl})
                </p>
              )}
            </div>
          )}
        </Karte>

        <Karte klasse="p-5">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Break-even</p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900 dark:text-slate-100">
            {euro(lage.breakEven.umsatzNettoProMonat)}<span className="text-sm font-normal text-slate-500"> netto / Monat</span>
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            So viel Umsatz deckt die Fixkosten. Rohertrag {lage.breakEven.rohertragsquote.toLocaleString('de-DE', { maximumFractionDigits: 0 })} % · {lage.breakEven.grundlage}
          </p>
          {fix.anzahl > 0 && monat.schnittEinnahmen > 0 && (
            <p className={`mt-3 text-sm ${monat.schnittEinnahmen >= lage.breakEven.umsatzNettoProMonat ? 'text-emerald-700 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-400'}`}>
              Ø Einnahmen der letzten 3 Monate: {euro(monat.schnittEinnahmen)}
              {monat.schnittEinnahmen >= lage.breakEven.umsatzNettoProMonat ? ' — darüber' : ' — darunter'}
            </p>
          )}
        </Karte>

        {lage.umsatzsteuer && (
          <Karte klasse="p-5">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Umsatzsteuer {monatsName(lage.umsatzsteuer.monat)}
            </p>
            <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900 dark:text-slate-100">
              {euro(lage.umsatzsteuer.zahllast)}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {lage.umsatzsteuer.zahllast >= 0 ? 'Zahllast' : 'Erstattung'} · fällig am {datum(lage.umsatzsteuer.faelligAm)}
            </p>
            <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
              {euro(lage.umsatzsteuer.umsatzsteuer)} Umsatzsteuer − {euro(lage.umsatzsteuer.vorsteuer)} Vorsteuer.
              Dieses Geld gehört dem Finanzamt — am besten gar nicht erst einplanen.
            </p>
          </Karte>
        )}
      </div>

      <Konten konten={liq.konten} nichtZugeordnet={liq.nichtZugeordnet} beiAenderung={laden} />
    </div>
  );
}

// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------

/** Konten: Stand eintragen und abgleichen. */
function Konten({ konten, nichtZugeordnet, beiAenderung }: {
  konten: Konto[]; nichtZugeordnet: number; beiAenderung: () => Promise<void>;
}) {
  const [neu, setNeu] = useState(false);
  const [abgleichFuer, setAbgleichFuer] = useState<Konto | null>(null);
  const [meldung, setMeldung] = useState<{ art: 'erfolg' | 'warnung'; text: string } | null>(null);

  return (
    <Karte>
      <KartenKopf titel="Konten"
                  hinweis="Stand vom Kontoauszug eintragen — die App rechnet mit jeder gebuchten Zahlung weiter."
                  aktion={<Knopf variante="primaer" className="!text-xs" onClick={() => { setNeu(true); setAbgleichFuer(null); }}>Konto hinzufügen</Knopf>} />

      {meldung && <div className="px-5 pt-4"><Blickfang ausloeser={meldung.text}><Hinweis art={meldung.art}>{meldung.text}</Hinweis></Blickfang></div>}

      {neu && (
        <KontoFormular
          beiAbbruch={() => setNeu(false)}
          beiErfolg={async () => { setNeu(false); setMeldung({ art: 'erfolg', text: 'Konto angelegt.' }); await beiAenderung(); }} />
      )}
      {abgleichFuer && (
        <AbgleichFormular konto={abgleichFuer}
          beiAbbruch={() => setAbgleichFuer(null)}
          beiErfolg={async (text, auffaellig) => {
            setAbgleichFuer(null);
            setMeldung({ art: auffaellig ? 'warnung' : 'erfolg', text });
            await beiAenderung();
          }} />
      )}

      {konten.length === 0 && !neu ? (
        <p className="px-5 py-6 text-sm text-slate-600 dark:text-slate-300">
          Noch kein Konto. Mit dem ersten eingetragenen Stand erscheinen Liquidität, Reichweite und Vorschau in Euro.
        </p>
      ) : (
        <ul className="divide-y divide-slate-200 dark:divide-slate-800">
          {konten.map(k => (
            <li key={k.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-2 font-medium text-slate-900 dark:text-slate-100">
                  {k.name}
                  <Abzeichen>{k.art === 'kasse' ? 'Kasse' : 'Bank'}</Abzeichen>
                  {k.hauptkonto && konten.filter(x => x.art === k.art).length > 1 && <Abzeichen art="blau">Hauptkonto</Abzeichen>}
                  {k.bankverbunden && <Abzeichen art="gruen">automatisch von der Bank</Abzeichen>}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {euro(k.standEingetragen)} am {k.standAm ? datum(k.standAm) : '—'}
                  {k.bewegungSeitdem !== 0 && ` · seitdem ${k.bewegungSeitdem > 0 ? '+' : '−'}${euro(Math.abs(k.bewegungSeitdem))}`}
                  {k.tageAlt !== null && k.tageAlt > 30 && (
                    <span className="text-amber-600 dark:text-amber-400"> · Stand ist {k.tageAlt} Tage alt</span>
                  )}
                </p>
              </div>
              <p className="tabular-nums text-lg font-semibold text-slate-900 dark:text-slate-100">{euro(k.standGeschaetzt)}</p>
              {k.bankverbunden
                ? <Link to="/bank" className="text-xs font-medium text-marke-700 hover:underline dark:text-marke-300">Umsätze</Link>
                : <Knopf className="!text-xs" onClick={() => { setAbgleichFuer(k); setNeu(false); }}>Abgleichen</Knopf>}
            </li>
          ))}
        </ul>
      )}

      {nichtZugeordnet !== 0 && (
        <p className="border-t border-slate-200 px-5 py-3 text-xs text-amber-700 dark:border-slate-800 dark:text-amber-400">
          {euro(Math.abs(nichtZugeordnet))} an Zahlungen passen zu keinem Konto — vermutlich Barzahlungen ohne angelegte Kasse.
        </p>
      )}
    </Karte>
  );
}

function KontoFormular({ beiAbbruch, beiErfolg }: { beiAbbruch: () => void; beiErfolg: () => Promise<void> }) {
  const [name, setName] = useState('Geschäftskonto');
  const [art, setArt] = useState<'bank' | 'kasse'>('bank');
  const [stand, setStand] = useState('');
  const [standAm, setStandAm] = useState(new Date().toISOString().slice(0, 10));
  const [fehler, setFehler] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);

  async function speichern(e: React.FormEvent) {
    e.preventDefault();
    setSendet(true); setFehler(null);
    try {
      await api('/konten', { methode: 'POST', daten: { name, art, stand: Number(stand.replace(/\./g, '').replace(',', '.')), standAm } });
      await beiErfolg();
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Konto konnte nicht angelegt werden.');
    } finally { setSendet(false); }
  }

  return (
    <Blickfang>
      <form onSubmit={speichern} className="space-y-3 border-b border-slate-200 bg-slate-50 px-5 py-4 dark:border-slate-800 dark:bg-slate-950/40">
        {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
        <div className="grid gap-3 sm:grid-cols-4">
          <Feld label="Name" value={name} onChange={e => setName(e.target.value)} required autoFocus />
          <Auswahl label="Art" value={art} onChange={e => {
            const a = e.target.value as 'bank' | 'kasse';
            setArt(a);
            if (name === 'Geschäftskonto' || name === 'Kasse') setName(a === 'kasse' ? 'Kasse' : 'Geschäftskonto');
          }}>
            <option value="bank">Bankkonto</option>
            <option value="kasse">Bargeldkasse</option>
          </Auswahl>
          <Feld label="Stand laut Auszug" value={stand} inputMode="decimal" placeholder="z. B. 12.450,00" required
                onChange={e => setStand(e.target.value)} />
          <Feld label="Stand am" type="date" value={standAm} max={new Date().toISOString().slice(0, 10)}
                onChange={e => setStandAm(e.target.value)} />
        </div>
        <div className="flex gap-2">
          <Knopf type="submit" variante="primaer" laedt={sendet}>Speichern</Knopf>
          <Knopf type="button" onClick={beiAbbruch}>Abbrechen</Knopf>
        </div>
      </form>
    </Blickfang>
  );
}

function AbgleichFormular({ konto, beiAbbruch, beiErfolg }: {
  konto: Konto; beiAbbruch: () => void; beiErfolg: (text: string, auffaellig: boolean) => Promise<void>;
}) {
  const [stand, setStand] = useState('');
  const [standAm, setStandAm] = useState(new Date().toISOString().slice(0, 10));
  const [fehler, setFehler] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);

  async function speichern(e: React.FormEvent) {
    e.preventDefault();
    setSendet(true); setFehler(null);
    try {
      const r = await api<{ abgleich: { geschaetzt: number; tatsaechlich: number; abweichung: number; auffaellig: boolean } }>(
        `/konten/${konto.id}/stand`,
        { methode: 'POST', daten: { stand: Number(stand.replace(/\./g, '').replace(',', '.')), standAm } });
      const a = r.abgleich;
      await beiErfolg(
        a.auffaellig
          ? `Abweichung bei ${konto.name}: Die App schätzte ${euro(a.geschaetzt)}, die Bank meldet ${euro(a.tatsaechlich)} `
            + `(${a.abweichung > 0 ? '+' : '−'}${euro(Math.abs(a.abweichung))}). `
            + (a.abweichung < 0
              ? 'Vermutlich fehlt eine Ausgabe in der Buchhaltung.'
              : 'Vermutlich ist eine bezahlte Rechnung noch nicht als bezahlt markiert.')
          : `${konto.name} abgeglichen — Schätzung und Bank stimmen überein.`,
        a.auffaellig);
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Abgleich fehlgeschlagen.');
    } finally { setSendet(false); }
  }

  return (
    <Blickfang>
      <form onSubmit={speichern} className="space-y-3 border-b border-slate-200 bg-slate-50 px-5 py-4 dark:border-slate-800 dark:bg-slate-950/40">
        <p className="text-sm text-slate-700 dark:text-slate-300">
          <strong>{konto.name}</strong> abgleichen. Die App schätzt gerade {euro(konto.standGeschaetzt)} —
          trag ein, was auf dem Kontoauszug steht.
        </p>
        {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
        <div className="grid gap-3 sm:grid-cols-3">
          <Feld label="Stand laut Auszug" value={stand} inputMode="decimal" required autoFocus
                onChange={e => setStand(e.target.value)} />
          <Feld label="Stand am" type="date" value={standAm}
                min={konto.standAm ?? undefined} max={new Date().toISOString().slice(0, 10)}
                onChange={e => setStandAm(e.target.value)} />
        </div>
        <div className="flex gap-2">
          <Knopf type="submit" variante="primaer" laedt={sendet}>Abgleichen</Knopf>
          <Knopf type="button" onClick={beiAbbruch}>Abbrechen</Knopf>
        </div>
      </form>
    </Blickfang>
  );
}
